import { Button } from '@capsule/consumer-design-system';

import { css } from 'styled-components';

import { getTransferableMeds } from 'src/app/components/PageSections/DoctorTransferForm/utils';
import type { EscriptProps, Medication, OptimisticPendingTransfer } from 'src/app/types';

import { GreenLink, List, ListItem, Number, SectionTitle } from '../../styles';
import { StyledMedicationList } from '../StyledMedicationList';

interface DefaultRxTransferProps {
  escriptCounts: EscriptProps[];
  isDoctorTransfer: boolean;
  medications: OptimisticPendingTransfer[];
  onChatClick: () => void;
  onContinue: () => void;
}

const DefaultRxTransfer = ({
  escriptCounts,
  isDoctorTransfer,
  medications,
  onChatClick,
  onContinue,
}: DefaultRxTransferProps) => {
  const isAllMedsTransfer = medications && medications.length > 0 && medications[0].name == 'All medications';
  const transferableMeds = getTransferableMeds(medications as unknown as Medication[]);

  if (!isAllMedsTransfer && transferableMeds.length === 0) {
    return (
      <>
        <div>The next time you speak to your doctor, tell them to send the following to Capsule: </div>

        <div
          css={css`
            margin: ${({ theme }) => theme.space.s7} 0;
          `}
        >
          <StyledMedicationList medications={medications} escriptCounts={escriptCounts} hideMedicationHeader />
        </div>

        <Button onClick={onContinue} variant="contained-secondary-lg">
          Done
        </Button>
      </>
    );
  }

  return (
    <>
      <SectionTitle>What's Next</SectionTitle>
      <List>
        <ListItem hasMarginBottom={true}>
          <Number>1.</Number>
          <div>
            We'll contact your {isDoctorTransfer ? 'doctor' : 'old pharmacy'} to move your meds over. This can take 1-2
            days. <GreenLink onClick={onChatClick}> Questions? Chat here</GreenLink>
          </div>
        </ListItem>
        <ListItem hasMarginBottom={true}>
          <Number>2.</Number>
          <div>We'll text you once ready to check out</div>
        </ListItem>
        <ListItem>
          <Number>3.</Number>
          <div>Pay your usual insurance copay and schedule your free delivery</div>
        </ListItem>
      </List>

      <div
        css={css`
          margin: ${({ theme }) => theme.space.s7} 0;
        `}
      >
        <SectionTitle>Your Medications</SectionTitle>
        <StyledMedicationList
          medications={isAllMedsTransfer ? [{ name: 'All medications' }] : transferableMeds}
          escriptCounts={escriptCounts}
          hideMedicationHeader
        />
      </div>

      <Button onClick={onContinue} variant="contained-secondary-lg">
        Continue
      </Button>
    </>
  );
};

interface RxTransferProps {
  escriptCounts: EscriptProps[];
  isDoctorTransfer: boolean;
  medications: OptimisticPendingTransfer[];
  onChatClick: () => void;
  onContinue: () => void;
}

export const RxTransfer = ({
  escriptCounts,
  isDoctorTransfer,
  medications,
  onChatClick,
  onContinue,
}: RxTransferProps) => {
  return (
    <DefaultRxTransfer
      escriptCounts={escriptCounts}
      isDoctorTransfer={isDoctorTransfer}
      medications={medications}
      onChatClick={onChatClick}
      onContinue={onContinue}
    />
  );
};
